import {
  Button,
  Empty,
  Input,
  Spin,
  Table,
  Tag,
  Toast,
  Typography,
} from "@douyinfe/semi-ui";
import { useEffect, useMemo, useState } from "react";
import {
  ApiClient,
  displayLeadAssignStatus,
  displayLeadStatus,
  displayLeadType,
  Lead,
} from "../../api/ApiClient";
import { formatDateTime } from "../../utils";
import { LeadDetailNew } from "./leadDetailNew";

const { Title, Text } = Typography;
const apiClient = ApiClient.getDefault();

const PAGE_SIZE = 20;

export default function FindLead() {
  const role = localStorage.getItem("role");
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [searchId, setSearchId] = useState("");
  const [searchedKeyword, setSearchedKeyword] = useState("");
  const [leads, setLeads] = useState<Lead[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [leadId, setLeadId] = useState<undefined | number>(undefined);
  const [viewMode, setViewMode] = useState(true);

  useEffect(() => {
    window.document.title = "查找客户";
  }, []);

  const doSearch = async (nextPage: number = 1, kw?: string) => {
    const finalKw = (kw ?? keyword ?? "").trim();
    const finalId = searchId.trim();
    if (!finalKw && !finalId) {
      Toast.warning("请输入搜索关键词或客户ID");
      return;
    }
    // monitor 角色按 localStorage 中的城市过滤；其它角色全库搜索
    let city: string | null = null;
    if (role === "monitor") {
      city = localStorage.getItem("city");
      setViewMode(false);
    }
    setLoading(true);
    try {
      const idNum = finalId ? Number(finalId) : null;
      const resp = await apiClient.searchAllLeads(
        finalKw,
        nextPage,
        PAGE_SIZE,
        city,
        idNum && !isNaN(idNum) ? idNum : undefined,
      );
      const data = resp.data;
      setLeads(data?.records ?? []);
      setTotal(data?.total ?? 0);
      setPage(nextPage);
      setSearchedKeyword(finalId ? `ID:${finalId}` : finalKw);
    } catch (e) {
      console.log(e);
      Toast.error("搜索失败");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    doSearch(1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const columns = useMemo(
    () => [
      {
        title: "客户ID",
        dataIndex: "id",
        width: 90,
      },
      {
        title: "姓名",
        dataIndex: "name",
        width: 100,
        render: (value: string, record: Lead) => (
          <Button
            theme="borderless"
            type="primary"
            onClick={() => setLeadId(record.id)}
          >
            {value || "-"}
          </Button>
        ),
      },
      {
        title: "手机号",
        dataIndex: "fullMobile",
        width: 140,
        render: (value: string) => value || "-",
      },
      {
        title: "客户类型",
        dataIndex: "leadType",
        width: 100,
        render: (value: string) => displayLeadType(value),
      },
      {
        title: "状态",
        dataIndex: "status",
        width: 110,
        render: (value: string) => {
          const text = displayLeadStatus(value);
          return <Tag color="blue">{text}</Tag>;
        },
      },
      {
        title: "分配状态",
        dataIndex: "assignStatus",
        width: 100,
        render: (value: string) => displayLeadAssignStatus(value),
      },
      {
        title: "城市",
        dataIndex: "applyCity",
        width: 80,
        render: (value: string) => value || "-",
      },
      {
        title: "渠道",
        dataIndex: "source",
        width: 100,
        render: (value: string) => value || "-",
      },
      {
        title: "团队",
        dataIndex: "teamName",
        width: 120,
        render: (value: any) => value || "-",
      },
      {
        title: "销售",
        dataIndex: "saleName",
        width: 100,
        render: (value: any) => value || "-",
      },
      {
        title: "星级",
        dataIndex: "star",
        width: 70,
        render: (value: number) => (value != null ? `${value}★` : "-"),
      },
      {
        title: "创建时间",
        dataIndex: "createdAt",
        width: 160,
        render: (value: any) => (value ? formatDateTime(value) : "-"),
      },
    ],
    [],
  );

  return (
    <div className="crud-page">
      <LeadDetailNew
        onOk={() => setLeadId(undefined)}
        onCancel={() => setLeadId(undefined)}
        leadId={leadId}
        viewMode={viewMode}
        findLeadMode={true}
      />
      <div className="crud-title">
        <Title heading={3}>查客户</Title>
      </div>
      <div className="crud-content">
        <div style={{ marginBottom: 16, display: "flex", gap: 8, alignItems: "center" }}>
          <Input
            prefix={<Text style={{width:60}} type="warning">关键词</Text>}
            placeholder={
              role === "monitor"
                ? "支持姓名 / 手机号 模糊搜索（范围：当前城市）"
                : "支持姓名 / 手机号 模糊搜索（范围：全库）"
            }
            value={keyword}
            onChange={(v) => setKeyword(v)}
            onKeyDown={handleKeyDown}
            style={{ width: 360 }}
            showClear
          />
          <Input
            prefix={<Text style={{width:60}} type="warning">客户ID</Text>}
            placeholder="输入客户ID精确查找"
            value={searchId}
            onChange={(v) => setSearchId(v)}
            onKeyDown={handleKeyDown}
            style={{ width: 220 }}
            showClear
          />
          <Button
            theme="solid"
            type="primary"
            loading={loading}
            onClick={handleSearch}
          >
            搜索
          </Button>
        </div>

        <Spin spinning={loading}>
          {leads.length === 0 && !loading && searchedKeyword ? (
            <Empty
              title={`没有找到与“${searchedKeyword}”相关的客户`}
              description="尝试更换关键词或检查输入"
            />
          ) : (
            <>
              <div style={{ marginBottom: 8 }}>
                <Text type="tertiary">
                  {searchedKeyword
                    ? `搜索“${searchedKeyword}”，共 ${total} 条结果`
                    : role === "monitor"
                      ? "请输入关键词进行搜索（范围：当前城市）"
                      : "请输入关键词进行搜索（范围：全库）"}
                </Text>
              </div>
              <Table<Lead>
                columns={columns as any}
                dataSource={leads}
                rowKey="id"
                pagination={{
                  currentPage: page,
                  pageSize: PAGE_SIZE,
                  total: total,
                  onPageChange: (p) => doSearch(p),
                  showSizeChanger: false,
                }}
                scroll={{ x: 1400 }}
                empty={
                  <Empty description="请输入关键词搜索客户（范围：全库）" />
                }
              />
            </>
          )}
        </Spin>
      </div>
    </div>
  );
}